import { Injectable, inject } from '@angular/core';
import { createStore, UseStore } from 'idb-keyval';
import { Observable, ReplaySubject } from 'rxjs';
import { NgrxStoreIdbOptions, OPTIONS } from './ngrx-store-idb.options';

/**
 * Owns the per-tenant idb-keyval store. When the tenant is known at
 * bootstrap (options.idb.bootstrapTenantId) the store is created eagerly;
 * otherwise creation is deferred until init() is called with the resolved
 * tenant (see RehydrateEffects.initTenantStore$). Consumers that must not
 * block (the metareducer) use getIfReady(); consumers that can wait
 * (rehydration, lock election) subscribe to store$.
 */
@Injectable({
  providedIn: 'root',
})
export class NgrxStoreIdbStoreProvider {
  private opts = inject<NgrxStoreIdbOptions>(OPTIONS);

  readonly bootstrapTenantId = this.opts.idb.bootstrapTenantId();

  private storeSubject = new ReplaySubject<UseStore>(1);

  readonly store$: Observable<UseStore> = this.storeSubject.asObservable();

  private idbStore: UseStore | null = null;

  private tenantId: string | null = null;

  constructor() {
    if (this.bootstrapTenantId != null) {
      this.init(this.bootstrapTenantId);
    }
  }

  get tenantIdInUse(): string | null {
    return this.tenantId;
  }

  /**
   * Creates the store for the given tenant. Idempotent: once a store
   * exists the tenant it was created for is authoritative for the rest of
   * the page's life — a different tenant requires a reload (mismatch guard).
   */
  init(tenantId: string): void {
    if (this.tenantId != null) {
      return;
    }
    this.tenantId = tenantId;
    this.idbStore = createStore(this.opts.idb.dbNameForTenant(tenantId), this.opts.idb.storeName);
    this.storeSubject.next(this.idbStore);
  }

  getIfReady(): UseStore | null {
    return this.idbStore;
  }
}
